import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Dropdown from "../common/Dropdown";
import Input from "../common/Input";
import Button from "../common/Button";
import Modal from "../common/Modal";
import {
  creativeFilterSchema,
  CreativeFilterFormData,
} from "../../schemas/creativeSchema";
import { useAdGroupOptions } from "../../hooks/useAdGroupOptions";
import { useBrandOptions } from "../../hooks/useBrandOptions";
import { useAuthPermissions } from "../../hooks/useAuthPermissions";

// The backend reads the literal "unassigned" as brand_id IS NULL.
const BRAND_FILTER_EXTRA_OPTIONS = [
  { label: "All", value: "" },
  { label: "Unassigned", value: "unassigned" },
];

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Active", value: "ACTIVE" },
  { label: "Upcoming", value: "UPCOMING" },
  { label: "Expired", value: "EXPIRED" },
];

interface CreativeFilterFormProps {
  visible: boolean;
  initialData?: Partial<CreativeFilterFormData>;
  onSubmit: (data: CreativeFilterFormData) => void;
  onCancel: () => void;
  onClose: () => void;
  isLoading?: boolean;
  // Optional callback to clear applied filters in parent while keeping the modal open
  onReset?: () => void;
}

const CreativeFilterForm: React.FC<CreativeFilterFormProps> = ({
  visible,
  initialData,
  onSubmit,
  onCancel,
  onClose,
  isLoading = false,
  onReset,
}) => {
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    trigger,
  } = useForm<CreativeFilterFormData>({
    resolver: zodResolver(creativeFilterSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: initialData,
  });

  const { adGroupOptions, isAdGroupOptionsLoading } = useAdGroupOptions();

  // Only tenants who can see brands get the filter — for everyone else the
  // query would 403 and the dropdown would sit there permanently empty.
  const { canView: canViewBrands } = useAuthPermissions("brands");
  const { brandOptions, isBrandOptionsLoading } = useBrandOptions({
    enabled: canViewBrands,
  });

  useEffect(() => {
    reset(initialData);
  }, [initialData, reset]);

  const handleReset = () => {
    // Clear form fields
    reset({});
    // Let parent clear its applied filters but keep modal open
    onReset?.();
  };

  const startDateValue = watch("startDate");
  const endDateValue = watch("endDate");

  const handleFormSubmit = (data: CreativeFilterFormData) => {
    // Get current form values in case they're not in data
    const formValues = {
      ...data,
      startDate: startDateValue || data.startDate,
      endDate: endDateValue || data.endDate,
    };
    onSubmit(formValues);
  };

  return (
    <Modal isOpen={visible} onClose={onClose} title="Filters">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <Dropdown
          label="Ad Group"
          options={adGroupOptions}
          value={watch("adGroupId")}
          name="adGroupId"
          onChange={(val) => setValue("adGroupId", val as string, { shouldValidate: false })}
          onBlur={() => trigger("adGroupId")}
          error={errors.adGroupId}
          disabled={isAdGroupOptionsLoading}
        />
        {canViewBrands && (
          <Dropdown
            label="Brand"
            options={BRAND_FILTER_EXTRA_OPTIONS.concat(brandOptions)}
            value={watch("brandId") ?? ""}
            name="brandId"
            searchable
            onChange={(val) =>
              setValue("brandId", (val as string) || undefined, {
                shouldValidate: false,
              })
            }
            onBlur={() => trigger("brandId")}
            error={errors.brandId}
            disabled={isBrandOptionsLoading}
          />
        )}
        <Dropdown
          label="Status"
          options={STATUS_OPTIONS}
          value={watch("status") ?? ""}
          name="status"
          onChange={(val) =>
            setValue("status", (val as string) || undefined, {
              shouldValidate: false,
            })
          }
          onBlur={() => trigger("status")}
          error={errors.status}
        />
        <Input
          label="Start Date"
          type="date"
          name="startDate"
          value={startDateValue || ""}
          onChange={(e) => setValue("startDate", e.target.value || undefined, { shouldValidate: false })}
          onBlur={() => trigger("startDate")}
          error={errors.startDate}
        />
        <Input
          label="End Date"
          type="date"
          name="endDate"
          value={endDateValue || ""}
          onChange={(e) => setValue("endDate", e.target.value || undefined, { shouldValidate: false })}
          onBlur={() => trigger("endDate")}
          error={errors.endDate}
        />

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleReset}
            disabled={isLoading}
          >
            Reset
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isLoading}
            className="w-full sm:w-auto"
          >
            Apply
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreativeFilterForm;
